/**
 * @file ListingDetailView.jsx
 * @description Kapsamlı ilan detay görünümü
 *
 * MCP server'dan gelen tüm ilan verilerini zengin bir
 * UI ile gösterir. Görsel galerisi, fiyat bilgisi,
 * özellikler, quickInfos, attributes ve iletişim bilgileri.
 *
 * @module widget/components/ListingDetailView
 */

import { ExternalLink, Loader2, MapPin, Phone, Sparkles } from "lucide-react";
import { Button } from "@openai/apps-sdk-ui/components/Button";
import {
  decodeHtmlEntities,
  formatPrice,
  formatRegionComparison,
  toEmlakjetUrl,
  toTelHref,
  openExternal,
} from "../utils";

import {
  DetailHeroImage,
  DetailImageThumbnails,
  DetailPropertyBadges,
  DetailQuickInfosGrid,
  DetailAttributes,
  DetailOwnerCard,
} from "./detail";

/** @constant {string} Emlakjet marka yeşili */
const EMLAKJET_GREEN = "#16a34a";

/**
 * İlan Detay Görünüm Bileşeni
 *
 * Tek bir ilanın tüm detaylarını gösteren ana görünüm.
 * Masaüstünde floating card, mobilde fullscreen olarak kullanılır.
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {Object|null} props.detail - İlan detay verisi
 * @param {Object} [props.detail.listing] - İlan bilgileri
 * @param {boolean} props.loading - Yükleniyor durumu
 * @param {number|null} props.regionAveragePrice - Bölge fiyat ortalaması
 * @param {number} props.selectedImageIndex - Seçili görsel indeksi
 * @param {Function} props.setSelectedImageIndex - Görsel seçim setter
 * @param {boolean} props.isDark - Karanlık mod
 * @param {boolean} [props.isFloatingCard=false] - Floating kart modu
 *
 * @returns {JSX.Element} Detay görünüm bileşeni
 *
 * @example
 * <ListingDetailView
 *   detail={listingDetail}
 *   loading={isLoading}
 *   regionAveragePrice={4500000}
 *   selectedImageIndex={0}
 *   setSelectedImageIndex={setIdx}
 *   isDark={false}
 *   isFloatingCard={true}
 * />
 *
 * @remarks
 * - Alt bileşenler: DetailHeroImage, DetailImageThumbnails,
 *   DetailPropertyBadges, DetailQuickInfosGrid, DetailAttributes,
 *   DetailOwnerCard
 * - HTML açıklama temizlenir ve truncate edilir
 * - Bölge karşılaştırması varsa yeşil badge gösterilir
 */
export function ListingDetailView({
  detail,
  loading,
  regionAveragePrice,
  selectedImageIndex,
  setSelectedImageIndex,
  isDark,
  isFloatingCard = false,
}) {
  const listing = detail?.listing ?? null;

  // Loading state
  if (loading) {
    return (
      <div className={`flex items-center justify-center h-full p-8 ${isDark ? "text-white/60" : "text-black/60"}`}>
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  // Empty state
  if (!listing) {
    return (
      <div className={`flex items-center justify-center h-full p-8 text-sm ${isDark ? "text-white/60" : "text-black/60"}`}>
        Detay bulunamadı.
      </div>
    );
  }

  // Extract data
  const href = toEmlakjetUrl(listing?.url);
  const priceLabel = formatPrice(listing?.price, listing?.currency);
  const images = Array.isArray(listing?.images)
    ? listing.images
    : listing?.image
    ? [listing.image]
    : [];
  const phone = listing?.phoneNumber ? String(listing.phoneNumber) : null;
  const telHref = phone ? toTelHref(phone) : null;

  const regionCompareLabel =
    regionAveragePrice != null
      ? formatRegionComparison(listing?.price, regionAveragePrice)
      : null;

  const cleanDescription = listing?.description
    ? decodeHtmlEntities(listing.description.replace(/<[^>]*>/g, " "))
        .replace(/\s+/g, " ")
        .trim()
    : null;

  // QuickInfos - filter out room, m2, floor (already shown as badges)
  const quickInfos = Array.isArray(listing?.quickInfos)
    ? listing.quickInfos.filter(
        (info) => !["room_count", "gross_square", "net_square", "floor_number"].includes(info.key)
      )
    : [];

  // Attributes - flatten into a single list
  const attributes = Array.isArray(listing?.attributes)
    ? listing.attributes.flatMap((attr) => attr.items || [])
    : [];

  return (
    <div className={`flex flex-col overflow-hidden ${isFloatingCard ? "" : "h-full"} ${isDark ? "bg-zinc-900" : "bg-white"}`}>
      <div className={isFloatingCard ? "" : "flex-1 min-h-0 overflow-y-auto"}>
        {/* Hero Image */}
        <DetailHeroImage
          images={images}
          selectedImageIndex={selectedImageIndex}
          setSelectedImageIndex={setSelectedImageIndex}
          priceLabel={priceLabel}
          squareMeterPrice={listing?.squareMeterPrice}
          fromOwner={listing?.fromOwner === true}
          priceChangeRate={listing?.priceChangeRate}
          href={href}
          phone={phone}
          telHref={telHref}
          isDark={isDark}
        />

        {/* Image Thumbnails */}
        <DetailImageThumbnails
          images={images}
          selectedImageIndex={selectedImageIndex}
          setSelectedImageIndex={setSelectedImageIndex}
          isDark={isDark}
        />

        {/* Content */}
        <div className="px-4 pt-4 pb-4 space-y-4">
          {/* Title */}
          <h2 className={`text-lg font-bold leading-tight ${isDark ? "text-white" : "text-gray-900"}`}>
            {listing?.title ?? "İlan"}
          </h2>

          {/* Property Badges */}
          <DetailPropertyBadges
            room={listing?.room}
            m2={listing?.m2}
            floor={listing?.floor}
            hasFiber={listing?.hasFiber === true}
            videoUrl={listing?.videoUrl}
            virtualTourUrl={listing?.virtualTourUrl}
            isDark={isDark}
          />

          {/* Location */}
          {listing?.location && (
            <div className={`flex items-center gap-2 text-sm ${isDark ? "text-white/60" : "text-gray-500"}`}>
              <MapPin className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
              {listing.location}
            </div>
          )}

          {/* Region Comparison */}
          {regionCompareLabel && (
            <div
              className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
              style={{
                backgroundColor: isDark ? "rgba(22, 163, 74, 0.2)" : "rgba(22, 163, 74, 0.1)",
                color: EMLAKJET_GREEN,
              }}
            >
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
              {regionCompareLabel}
            </div>
          )}

          {/* Quick Infos */}
          <DetailQuickInfosGrid quickInfos={quickInfos} isDark={isDark} />

          {/* Attributes */}
          <DetailAttributes attributes={attributes} isDark={isDark} />

          {/* Owner Card */}
          <DetailOwnerCard
            ownerName={listing?.ownerName}
            ownerType={listing?.ownerType}
            ownerLogo={listing?.ownerLogo}
            ownerVerified={listing?.ownerVerified === true}
            isDark={isDark}
          />

          {/* Description */}
          {cleanDescription && (
            <div>
              <div className={`text-xs font-semibold mb-1.5 ${isDark ? "text-white/60" : "text-gray-500"}`}>
                Açıklama
              </div>
              <p className={`text-sm leading-relaxed ${isDark ? "text-white/70" : "text-gray-600"}`}>
                {cleanDescription.length > 300 ? cleanDescription.slice(0, 300) + "..." : cleanDescription}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={() => openExternal(href)}
              disabled={!href}
              className="flex-1"
              color="success"
              variant="solid"
            >
              <ExternalLink className="h-4 w-4" strokeWidth={2} />
              <span className="ml-2">Emlakjet'te aç</span>
            </Button>
            {phone && (
              <Button
                color="success"
                variant="outline"
                onClick={() => openExternal(telHref)}
                className="flex-1"
              >
                <Phone className="h-4 w-4" strokeWidth={1.5} />
                <span className="ml-2">Ara</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ListingDetailView;
